import {
    ComponentFactoryResolver,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import { getCaretPosition, getValue, insertValue, setCaretPosition } from './mention-utils';

import { UserHelper } from '../../../helpers/user.helper';
import { Member } from '../../../models/clubs/member.model';
import { MentionMember } from '../../../models/clubs/mention-member.model';
import { MentionConfig } from './mention-config';
import { MentionListComponent } from './mention-list.component';

const KEY_BACKSPACE = 8;
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_SHIFT = 16;
const KEY_ESCAPE = 27;
const KEY_SPACE = 32;
//const KEY_LEFT = 37;
const KEY_UP = 38;
//const KEY_RIGHT = 39;
const KEY_DOWN = 40;
const KEY_BUFFERED = 229;
/**
 * Angular Mentions.
 * https://github.com/dmacfarlane/angular-mentions
 *
 * Copyright (c) 2017 Dan MacFarlane
 */
@Directive({
    selector: '[mention], [mentionConfig]',
    host: {
        '(keydown)': 'keyHandler($event)',
        '(input)': 'inputHandler($event)',
        '(blur)': 'blurHandler($event)',
        // Removed (touchstart) as it shouldn't trigger key/input logic
        autocomplete: 'off',
    },
})
export class MentionDirective implements OnChanges {
    // stores the items passed to the mentions directive and used to populate the root items in mentionConfig
    private mentionItems: Member[];

    @Input() set mention(items: Member[]) {
        this.mentionItems = items;
    }

    // the provided configuration object
    @Input() mentionConfig: MentionConfig = { items: [] };

    @Output() mentionSelectedEvent: EventEmitter<MentionMember> = new EventEmitter<MentionMember>();

    private activeConfig: MentionConfig;

    private DEFAULT_CONFIG: MentionConfig = {
        items: [],
        triggerChar: '@',
        labelKey: 'searchFullName',
        maxItems: 1000,
        allowSpace: true,
        returnTrigger: false,
        mentionSelect: (item: Member) => {
            const tagUser = this.activeConfig.triggerChar + this.userHelper.getFullName(item);
            this.mentionSelectedEvent.emit({ tagMember: tagUser, userId: item.id } as MentionMember);
            return `${tagUser} `;
        },
        mentionFilter: (searchString: string, items: Member[]) => {
            const searchStringLowerCase = searchString.toLocaleLowerCase().trim();
            if (!searchStringLowerCase) {
                return items;
            }
            return items.filter((member) => {
                const fullName = `${member.firstName} ${member.lastName}`.toLocaleLowerCase();
                return fullName.includes(searchStringLowerCase);
            });
        },
    };

    // template to use for rendering list items
    @Input() mentionListTemplate: TemplateRef<any>;

    // event emitted whenever the search term changes
    @Output() searchTerm = new EventEmitter<string>();

    // event emitted when an item is selected
    @Output() itemSelected = new EventEmitter<any>();

    // event emitted whenever the mention list is opened or closed
    @Output() opened = new EventEmitter();
    @Output() closed = new EventEmitter();

    private triggerChars: { [key: string]: MentionConfig } = {};

    private searchString: string;
    private content = ''; // This property seems unused outside of keyHandler, but keeping it for now
    private startPos: number;
    private startNode: any;
    private searchList: MentionListComponent;
    private searching: boolean;
    private iframe: any; // optional
    private lastKeyCode: number;

    constructor(
        private _element: ElementRef,
        private _componentResolver: ComponentFactoryResolver,
        private _viewContainerRef: ViewContainerRef,
        private userHelper: UserHelper,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['mention'] || changes['mentionConfig']) {
            this.updateConfig();
        }
    }

    public updateConfig() {
        const config = this.mentionConfig;
        this.triggerChars = {};
        // use items from directive if they have been set
        if (this.mentionItems) {
            config.items = this.mentionItems;
        }
        this.addConfig(config);
        // nested configs
        if (config.mentions) {
            config.mentions.forEach((config) => this.addConfig(config));
        }
    }

    // add configuration for a trigger char
    private addConfig(config: MentionConfig) {
        // defaults
        const defaults = Object.assign({}, this.DEFAULT_CONFIG);
        config = Object.assign(defaults, config);
        // items
        let items = config.items;
        if (items && items.length > 0) {
            // convert strings to objects
            if (typeof items[0] === 'object') {
                items = items
                    .filter((item) => item != null || item != undefined)
                    .map((user: any) => {
                        const object = user;
                        // Assuming toCaseInsensitive() is defined globally or via prototype
                        object[config.labelKey] = this.userHelper.getFullName(user).toCaseInsensitive();
                        return object;
                    });
            }
            if (config.labelKey) {
                // remove items without an labelKey (as it's required to filter the list)
                items = items.filter((e) => e[config.labelKey]);
                if (!config.disableSort) {
                    items.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
                }
            }
        }
        config.items = items;

        // add the config
        this.triggerChars[config.triggerChar] = config;

        // for async update while menu/search is active
        if (this.activeConfig && this.activeConfig.triggerChar == config.triggerChar) {
            this.activeConfig = config;
            this.updateSearchList();
        }
    }

    setIframe(iframe: HTMLIFrameElement) {
        this.iframe = iframe;
    }

    stopEvent(event: any) {
        //if (event instanceof KeyboardEvent) { // does not work for iframe
        if (!event.wasClick) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
    }

    blurHandler(event: any) {
        this.stopEvent(event);
        this.stopSearch();
    }

    // *** FIX: Use inputHandler to reliably capture text changes (mobile, IME, paste) ***
    inputHandler(event: any, nativeElement: HTMLInputElement = this._element.nativeElement) {
        const val: string = getValue(nativeElement);
        const pos = getCaretPosition(nativeElement, this.iframe);

        // 1. Check for trigger character input via composition/mobile.
        // We use event.data for the inserted character.
        if (event.inputType === 'insertText' && this.triggerChars[event.data] && !this.searching) {
            // Start the search, treating this like a keydown that triggered it
            this.activeConfig = this.triggerChars[event.data];
            this.startPos = pos - 1; // Caret position is AFTER the character
            this.startNode = (this.iframe ? this.iframe.contentWindow.getSelection() : window.getSelection()).anchorNode;
            this.searching = true;
            this.searchString = null;
            this.showSearchList(nativeElement);
            this.updateSearchList();

            if (this.activeConfig.returnTrigger) {
                this.searchTerm.emit(this.activeConfig.triggerChar);
            }
            return;
        }

        // 2. Handle searching/filtering for ALL text input when active
        if (this.searching) {
            if (pos <= this.startPos) {
                // If caret moves back to or before the trigger char, stop search
                this.stopSearch();
                return;
            }

            // Recalculate mention from the current value and start position
            const mention = val.substring(this.startPos + 1, pos);

            // Check for termination characters (like space if not allowed)
            if (!this.activeConfig.allowSpace && mention.includes(' ')) {
                this.stopSearch();
                return;
            }

            this.searchString = mention;

            // Emit and update search list
            if (this.activeConfig.returnTrigger) {
                const triggerChar = val.substring(this.startPos, this.startPos + 1);
                this.searchTerm.emit(triggerChar + this.searchString);
            } else {
                this.searchTerm.emit(this.searchString);
            }

            this.updateSearchList();
        }

        // This is necessary to clear the old 'content' property if it's used for other logic
        // Though 'content' usage in keyHandler is primarily for trigger start logic
        if ([KEY_ENTER, KEY_SPACE, KEY_TAB].includes(this.lastKeyCode) && this.content !== '') {
            this.content = '';
        }
        if (this.lastKeyCode === KEY_BACKSPACE && this.content !== '') {
            this.content = this.content.slice(0, -1);
        }
    }

    // @param nativeElement is the alternative text element in an iframe scenario
    keyHandler(event: any, nativeElement: HTMLInputElement = this._element.nativeElement) {
        this.lastKeyCode = event.keyCode || event.data;

        // Gestion des compositions IME et des événements buffered
        if (event.isComposing || this.lastKeyCode === KEY_BUFFERED) {
            return;
        }

        const val: string = getValue(nativeElement);
        let pos = getCaretPosition(nativeElement, this.iframe);
        let charPressed = event.key || event.data;

        // Add this check to close the mention list when @ is deleted
        if (this.searching && this.lastKeyCode === KEY_BACKSPACE) {
            const currentTriggerCharPos = this.startPos;
            if (pos <= currentTriggerCharPos) {
                this.stopSearch();
                return;
            }
        }

        if (charPressed) {
            const charCode = event.which || this.lastKeyCode;
            if (!event.shiftKey && charCode >= 65 && charCode <= 90) {
                charPressed = String.fromCharCode(charCode + 32);
                this.content += charPressed;
            }
        }

        if (this.lastKeyCode == KEY_ENTER && event.wasClick && pos < this.startPos) {
            pos = this.startNode.length;
            setCaretPosition(this.startNode, pos, this.iframe);
        }
        const config = this.triggerChars[charPressed];
        const previousContent = this.content;

        // START SEARCH: Only rely on keydown for desktop/non-IME start, or if inputHandler didn't catch it
        if (config && (previousContent === '' || event.inputEvent)) {
            this.activeConfig = config;
            this.startPos = event.inputEvent ? pos - 1 : pos;
            this.startNode = (this.iframe ? this.iframe.contentWindow.getSelection() : window.getSelection()).anchorNode;
            this.searching = true;
            this.searchString = null;
            this.showSearchList(nativeElement);
            this.updateSearchList();

            if (config.returnTrigger) {
                this.searchTerm.emit(config.triggerChar);
            }
        }

        // NAVIGATION AND SELECTION: Only handle control keys when searching
        else if (this.startPos >= 0 && this.searching) {
            if (this.lastKeyCode !== KEY_SHIFT && !event.metaKey && !event.altKey && !event.ctrlKey && pos > this.startPos) {
                // Selection Keys (Enter/Tab/Escape)
                if (!this.searchList.hidden) {
                    if (this.lastKeyCode === KEY_TAB || this.lastKeyCode === KEY_ENTER) {
                        this.stopEvent(event);
                        this.itemSelected.emit(this.searchList.activeItem);

                        const text = this.activeConfig.mentionSelect(this.searchList.activeItem, this.activeConfig.triggerChar);
                        this.content = '';

                        // Use a short delay to ensure DOM is updated before inserting value
                        // This helps with contentEditable/iframe issues, especially after mobile input
                        setTimeout(() => {
                            // Insert the mention text first
                            insertValue(nativeElement, this.startPos, pos, text, this.iframe);

                            // Close the mention list and reset all state
                            this.startPos = -1;
                            this.searching = false;
                            this.content = '';
                            this.stopSearch();

                            // Ensure the input field is fully focused and functional
                            nativeElement.focus();

                            // Set the correct caret position
                            const newValue = getValue(nativeElement);
                            const correctCaretPosition = this.startPos + text.length;
                            const safeCaretPosition = Math.min(correctCaretPosition, newValue.length);

                            // Use direct DOM manipulation to ensure proper state
                            if (nativeElement.setSelectionRange) {
                                nativeElement.setSelectionRange(safeCaretPosition, safeCaretPosition);
                            }

                            // Force a reflow to ensure all changes are applied
                            void nativeElement.offsetHeight;

                            // Trigger input event to ensure Angular's change detection
                            if ('createEvent' in document) {
                                const evt = document.createEvent('HTMLEvents');
                                if (this.iframe) {
                                    evt.initEvent('change', true, false);
                                } else {
                                    evt.initEvent('input', true, false);
                                }
                                this._element.nativeElement.dispatchEvent(evt);
                            }
                        }, 10);

                        return false;
                    } else if (this.lastKeyCode === KEY_ESCAPE) {
                        this.stopEvent(event);
                        this.stopSearch();
                        return false;
                    }

                    // Navigation Keys (Down/Up)
                    else if (this.lastKeyCode === KEY_DOWN) {
                        this.stopEvent(event);
                        this.searchList.activateNextItem();
                        return false;
                    } else if (this.lastKeyCode === KEY_UP) {
                        this.stopEvent(event);
                        this.searchList.activatePreviousItem();
                        return false;
                    }
                }

                // Backspace/Space/Caret Position logic to stop search
                if (this.lastKeyCode === KEY_BACKSPACE && pos > 0) {
                    pos--;
                    // The inputHandler already handles the stopSearch if pos == this.startPos after deletion
                }
                // No need for the original complex search string calculation here, as inputHandler handles it

                // Stop any other keydown events during search that aren't control/navigation keys
                if (charPressed.length != 1 && this.lastKeyCode != KEY_BACKSPACE) {
                    this.stopEvent(event);
                    return false;
                }
            }
        }

        // Clean up internal content tracker on common termination/seperation keys
        if ([KEY_ENTER, KEY_SPACE, KEY_TAB].includes(this.lastKeyCode) && previousContent !== '') {
            this.content = '';
        }

        if (this.lastKeyCode === KEY_BACKSPACE && previousContent !== '') {
            this.content = this.content.slice(0, -1);
        }

        // Ensure normal keyboard behavior is preserved for Enter key when not in search mode
        if (this.lastKeyCode === KEY_ENTER && !this.searching && !event.wasClick) {
            // Allow normal Enter key behavior when not searching
            return true;
        }
    }

    // exposed for external calls to open the mention list, e.g. by clicking a button
    public startSearch(triggerChar?: string, nativeElement: HTMLInputElement = this._element.nativeElement) {
        triggerChar = triggerChar || this.mentionConfig.triggerChar || this.DEFAULT_CONFIG.triggerChar;
        const pos = getCaretPosition(nativeElement, this.iframe);
        insertValue(nativeElement, pos, pos, triggerChar, this.iframe);
        // Simulate input event to trigger the start search logic in inputHandler
        this.inputHandler({ inputType: 'insertText', data: triggerChar }, nativeElement);
    }

    stopSearch() {
        if (this.searchList && !this.searchList.hidden) {
            this.searchList.hidden = true;
            this.closed.emit();
        }
        this.activeConfig = null;
        this.searching = false;
        this.content = ''; // ensure content is reset too
    }

    updateSearchList() {
        let matches: any[] = [];
        if (this.activeConfig && this.activeConfig.items) {
            let objects = this.activeConfig.items;
            // disabling the search relies on the async operation to do the filtering
            if (!this.activeConfig.disableSearch && this.searchString && this.activeConfig.labelKey) {
                if (this.activeConfig.mentionFilter) {
                    objects = this.activeConfig.mentionFilter(this.searchString, objects);
                }
            }
            matches = objects;
            if (this.activeConfig.maxItems > 0) {
                matches = matches.slice(0, this.activeConfig.maxItems);
            }
        }
        // update the search list
        if (this.searchList) {
            this.searchList.items = matches;
            this.searchList.hidden = matches.length == 0;
        }
    }

    showSearchList(nativeElement: HTMLInputElement) {
        this.opened.emit();

        if (this.searchList == null) {
            const componentFactory = this._componentResolver.resolveComponentFactory(MentionListComponent);
            const componentRef = this._viewContainerRef.createComponent(componentFactory);
            if (this.mentionConfig.appendTo === 'body') {
                document.body.appendChild(componentRef.location.nativeElement);
            }
            this.searchList = componentRef.instance;
            this.searchList.itemTemplate = this.mentionListTemplate;
            componentRef.instance['itemClick'].subscribe(() => {
                nativeElement.focus();
                const fakeKeydown = { key: 'Enter', keyCode: KEY_ENTER, wasClick: true };
                this.keyHandler(fakeKeydown, nativeElement);
            });
        }
        this.searchList.labelKey = this.activeConfig.labelKey;
        this.searchList.dropUp = this.mentionConfig.dropUp && window.innerWidth <= 768;
        this.searchList.styleOff = this.mentionConfig.disableStyle;
        this.searchList.activeIndex = 0;
        this.searchList.position(nativeElement, this.iframe);
        window.requestAnimationFrame(() => this.searchList.reset());
    }
}
